/**
 * AiFeedbackButton — reusable inline button that fetches AI pedagogical
 * feedback for a single written or spoken artefact and shows the result
 * underneath. Used in:
 *   - CommunityPage (Schreiben) → posts with category="writing"
 *   - MaterialComments under Sprechen materials → audio comments
 *
 * Props:
 *   - postId | commentId : exactly ONE of these. Determines the endpoint.
 *   - cachedFeedback     : optional initial feedback (server-cached). If set
 *                          the result panel is shown collapsed straight away.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Spinner, CaretDown, CaretUp, Trophy, Star } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiFeedbackButton({ postId, commentId, cachedFeedback }) {
  const [busy, setBusy] = useState(false);
  const [fb, setFb] = useState(cachedFeedback || null);
  const [open, setOpen] = useState(!!cachedFeedback);

  const isSpeaking = !!commentId;
  const endpoint = isSpeaking
    ? `${API}/ai/feedback/speaking/${commentId}`
    : `${API}/ai/feedback/writing/${postId}`;

  const fetchFeedback = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(endpoint, {}, { withCredentials: true });
      setFb(res.data || null);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim KI-Feedback');
    } finally {
      setBusy(false);
    }
  };

  if (!fb) {
    return (
      <button
        onClick={fetchFeedback}
        disabled={busy}
        className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 transition-colors disabled:opacity-50"
        data-testid={`ai-feedback-btn-${postId || commentId}`}
      >
        {busy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
        <span>{busy ? 'KI denkt…' : 'KI-Feedback'}</span>
      </button>
    );
  }

  return (
    <div className="w-full" data-testid={`ai-feedback-panel-${postId || commentId}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 transition-colors"
      >
        <Sparkle size={14} weight="fill" />
        <span>KI-Feedback</span>
        {fb.score != null && (
          <span className="ml-1 inline-flex items-center gap-0.5 bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
            <Trophy size={10} weight="fill" /> {fb.score}/10
          </span>
        )}
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      {open && (
        <div className="mt-2 bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-xl p-3 text-sm space-y-2">
          {fb.transcript && (
            <p className="text-xs text-[#78716C] italic">
              <span className="font-semibold not-italic">Transkription:</span> {fb.transcript}
            </p>
          )}
          {fb.summary && <p className="text-[#292524]">{fb.summary}</p>}
          {fb.corrected && (
            <div className="bg-white rounded-lg border border-stone-200 p-2">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-[#78716C] mb-1">Korrigierte Version</p>
              <p className="text-[#292524] whitespace-pre-wrap">{fb.corrected}</p>
            </div>
          )}
          {Array.isArray(fb.tips) && fb.tips.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-[#78716C] mb-1">Tipps</p>
              <ul className="space-y-1">
                {fb.tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[#292524]">
                    <Star size={11} weight="fill" className="text-amber-500 flex-shrink-0 mt-1" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
